import type {
  PaymentInformation,
  RecurlyPaymentCreate,
  RecurlyPaymentInformation,
  RecurlyPaymentUpdate
} from '~/types/account'
import BaseProxyController from '~/service/base'
import type { DemoPaymentInformation } from '~/types/demo'
import { $api } from '~/composables/api'

export abstract class PaymentServiceBase {
  BASE_URL = 'payment/recurly'

  async createPaymentMethod (payment: RecurlyPaymentCreate, recaptchaToken?: string, recaptchaAction?: string) {
    const paymentUrl = `${this.BASE_URL}/${payment.recurlyToken}`
    const query: { [id: string]: string } = {}
    if (recaptchaToken) {
      query.recaptcha_token = recaptchaToken
      if (recaptchaAction) {
        query.recaptcha_action = recaptchaAction
      }
    }
    query.isDefaultPayment = payment.isDefaultPayment.toString()
    const savedPayment = await $api()(paymentUrl, { method: 'POST', body: {}, query })
    return savedPayment
  }

  async updatePaymentMethod (payment: RecurlyPaymentUpdate, countryCode: string) {
    const recurlyPayment = payment
    if (!recurlyPayment.country) {
      payment = { ...payment, country: countryCode }
    }

    // If country is OTHER, send essential fields with domain country and empty address fields
    // OTHER is just an indicator - we still need to send the domain country for Recurly billing
    // IMPORTANT: We must explicitly send empty strings for address fields because Recurly only
    // updates the fields that are passed in the request. Omitting fields would leave old data unchanged.
    let payloadBody = payment
    if (payment.country === 'OTHER') {
      payloadBody = {
        billingInfoId: payment.billingInfoId,
        firstName: payment.firstName,
        lastName: payment.lastName,
        country: countryCode, // Use domain country, not 'OTHER'
        addressLine1: '',
        addressLine2: '',
        city: '',
        region: '',
        postalCode: '',
        phoneNumber: '',
        isDefaultPayment: payment.isDefaultPayment,
        isOwnedByDemo: payment.isOwnedByDemo
      }
    }

    const paymentUrl = `${this.BASE_URL}/${payment.billingInfoId}`
    const savedPayment = await $api()<RecurlyPaymentInformation>(paymentUrl, { method: 'PUT', body: payloadBody })
    return savedPayment
  }

  async deletePaymentMethod (payment: PaymentInformation | DemoPaymentInformation) {
    const paymentUrl = `${this.BASE_URL}/${payment.billingInfoId}`

    // isOwnedByDemo comes down with demo payment information, flag is necessary
    // to pass back up to delete legacy demonstrator credit cards,
    // cards that have been added in this system will have isOwnedByDemo is false
    await $api()(paymentUrl, { method: 'DELETE', query: { isOwnedByDemo: (payment as DemoPaymentInformation)?.isOwnedByDemo ? 'true' : undefined } })
  }
}

export class PaymentService extends PaymentServiceBase {

}

/* istanbul ignore next */
class DemoPaymentService extends PaymentServiceBase {
  override BASE_URL = 'account/demo/payment/recurly'
}

/* istanbul ignore next */
export default () => {
  const checkoutProxyController = new BaseProxyController( {
    DemoService: DemoPaymentService,
    UserService: PaymentService
  })
  const proxy = new Proxy<PaymentServiceBase>({} as unknown as PaymentServiceBase, checkoutProxyController)
  return proxy
}
